import axios from 'axios';

const API_URL = 'https://api.ironwall.in/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Auto attach token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ironwall_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto handle token expiry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ironwall_token');
      localStorage.removeItem('ironwall_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// AUTH
export const authAPI = {
  adminLogin: (data) =>
    api.post('/auth/admin/login', data),
  vendorLogin: (data) =>
    api.post('/auth/vendor/login', data),
  vendorRegister: (data) =>
    api.post('/auth/vendor/register', data),
  verifyOtp: (data) =>
    api.post('/auth/vendor/verify-otp', data),
  resendOtp: (data) =>
    api.post('/auth/vendor/resend-otp', data),
  getProfile: () =>
    api.get('/auth/profile'),
  changePassword: (data) =>
    api.post('/auth/change-password', data),
};

// ADMIN
export const adminAPI = {
  getStats: () =>
    api.get('/admin/stats'),
  getVendors: (params) =>
    api.get('/admin/vendors', { params }),
  getPendingVendors: () =>
    api.get('/admin/vendors/pending'),
  getVendorDetail: (id) =>
    api.get(`/admin/vendors/${id}`),
  approveVendor: (id) =>
    api.patch(`/admin/vendors/${id}/approve`),
  rejectVendor: (id, data) =>
    api.patch(`/admin/vendors/${id}/reject`, data),
  suspendVendor: (id, data) =>
    api.patch(`/admin/vendors/${id}/suspend`, data),
  reactivateVendor: (id) =>
    api.patch(`/admin/vendors/${id}/reactivate`),
  updateCredits: (id, data) =>
    api.patch(`/admin/vendors/${id}/credits`, data),
  getAdmins: () =>
    api.get('/admin/admins'),
  createAdmin: (data) =>
    api.post('/admin/admins', data),
};

// VENDORS
export const vendorAPI = {
  getProfile: () =>
    api.get('/vendors/profile'),
  updateProfile: (data) =>
    api.put('/vendors/profile', data),
  getUsers: () =>
    api.get('/vendors/users'),
  addUser: (data) =>
    api.post('/vendors/users', data),
  updateUser: (id, data) =>
    api.put(`/vendors/users/${id}`, data),
  getCategories: () =>
    api.get('/vendors/categories'),
};

// ENGINEERS
export const engineerAPI = {
  getAll: (params) =>
    api.get('/engineers', { params }),
  getOne: (id) =>
    api.get(`/engineers/${id}`),
  add: (data) =>
    api.post('/engineers', data),
  update: (id, data) =>
    api.put(`/engineers/${id}`, data),
  deactivate: (id) =>
    api.patch(`/engineers/${id}/deactivate`),
  resetPassword: (id, data) =>
    api.patch(`/engineers/${id}/reset-password`,
      data),
  getLiveLocations: () =>
    api.get('/engineers/live-locations'),
};

// CUSTOMERS
export const customerAPI = {
  getAll: (params) =>
    api.get('/customers', { params }),
  getOne: (id) =>
    api.get(`/customers/${id}`),
  add: (data) =>
    api.post('/customers', data),
  update: (id, data) =>
    api.put(`/customers/${id}`, data),
  addAddress: (id, data) =>
    api.post(`/customers/${id}/addresses`, data),
  delete: (id) =>
    api.delete(`/customers/${id}`),
};

// INVENTORY
export const inventoryAPI = {
  getAll: (params) =>
    api.get('/inventory', { params }),
  getOne: (id) =>
    api.get(`/inventory/${id}`),
  add: (data) =>
    api.post('/inventory', data),
  update: (id, data) =>
    api.put(`/inventory/${id}`, data),
  getSummary: () =>
    api.get('/inventory/summary'),
  getCatalog: (params) =>
    api.get('/catalog', { params }),
};

// TICKETS
export const ticketAPI = {
  getAll: (params) =>
    api.get('/tickets', { params }),
  getOne: (id) =>
    api.get(`/tickets/${id}`),
  create: (data) =>
    api.post('/tickets', data),
  assign: (id, data) =>
    api.patch(`/tickets/${id}/assign`, data),
  updateStatus: (id, data) =>
    api.patch(`/tickets/${id}/status`, data),
  close: (id, data) =>
    api.patch(`/tickets/${id}/close`, data),
  getProof: (id) =>
    api.get(`/proofs/ticket/${id}`),
};

// REPORTS
export const reportsAPI = {
  getDashboard: () =>
    api.get('/reports/dashboard'),
  getDeliveries: (params) =>
    api.get('/reports/deliveries', { params }),
  getEngineers: (params) =>
    api.get('/reports/engineers', { params }),
  getInventory: () =>
    api.get('/reports/inventory'),
  getSLA: (params) =>
    api.get('/reports/sla', { params }),
  getCustomers: () =>
    api.get('/reports/customers'),
};

// LOCATION
export const locationAPI = {
  getLive: () =>
    api.get('/location/live'),
  getHistory: (engineerId, params) =>
    api.get(`/location/history/${engineerId}`,
      { params }),
};

export default api;